import { memo, useCallback, useEffect, useRef, useState, type DragEvent } from 'react';
import { ArrowDown, ArrowUp, ChevronDown, ChevronRight, ChevronsDownUp, ChevronsUpDown, MoreHorizontal, Trash2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/Button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/DropdownMenu';
import { GRID_COLUMNS, GRID_GAP_PX, GRID_ROW_PX, type DashboardSection, type WidgetKind } from '@/features/intelligence/dashboard/types';
import { AddWidgetMenu } from './AddWidgetMenu';
import { useDashboard } from './DashboardContext';
import { WidgetFrame, type GridMetrics, type WidgetFrameHandlers } from './WidgetFrame';

export interface DropTarget {
  sectionId: string;
  index: number;
}

export interface SectionHandlers extends WidgetFrameHandlers {
  onDragOverSection: (event: DragEvent, sectionId: string, endIndex: number) => void;
  onAddWidget: (kind: WidgetKind, sectionId: string) => void;
  onRenameSection: (sectionId: string, title: string) => void;
  onDescribeSection: (sectionId: string, description: string) => void;
  onToggleCollapse: (sectionId: string) => void;
  onMoveSection: (sectionId: string, direction: -1 | 1) => void;
  onRemoveSection: (sectionId: string) => void;
}

interface DashboardSectionViewProps {
  section: DashboardSection;
  isFirst: boolean;
  isLast: boolean;
  dragWidgetId: string | null;
  dropTarget: DropTarget | null;
  handlers: SectionHandlers;
}

/** Text input that keeps its own buffer and commits on blur / Enter, so typing doesn't churn the layout. */
function InlineText({
  value,
  onCommit,
  placeholder,
  className,
  ariaLabel,
}: {
  value: string;
  onCommit: (next: string) => void;
  placeholder?: string;
  className?: string;
  ariaLabel: string;
}) {
  const [text, setText] = useState(value);
  useEffect(() => setText(value), [value]);
  const commit = () => {
    const next = text.trim();
    if (next !== value) onCommit(next);
  };
  return (
    <input
      value={text}
      aria-label={ariaLabel}
      placeholder={placeholder}
      onChange={(e) => setText(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          e.currentTarget.blur();
        } else if (e.key === 'Escape') {
          setText(value);
          e.currentTarget.blur();
        }
      }}
      className={cn(
        'w-full min-w-0 rounded-sm border-b border-dashed border-transparent bg-transparent outline-none transition-colors placeholder:text-muted-foreground/70 hover:border-border focus:border-ring',
        className,
      )}
    />
  );
}

function DashboardSectionViewImpl({ section, isFirst, isLast, dragWidgetId, dropTarget, handlers }: DashboardSectionViewProps) {
  const { editing, isMobile } = useDashboard();
  const gridRef = useRef<HTMLDivElement>(null);
  const collapsed = !!section.collapsed;
  const count = section.widgets.length;
  const dragging = dragWidgetId !== null;
  const endIndex = count;
  const targetsEnd = dropTarget?.sectionId === section.id && dropTarget.index === endIndex;

  const getGridMetrics = useCallback((): GridMetrics | null => {
    const el = gridRef.current;
    if (!el) return null;
    const colWidth = (el.clientWidth - GRID_GAP_PX * (GRID_COLUMNS - 1)) / GRID_COLUMNS;
    return { colWidth, rowHeight: GRID_ROW_PX, gap: GRID_GAP_PX };
  }, []);

  return (
    <section aria-label={section.title} className="space-y-3">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          {editing ? (
            <>
              <InlineText
                value={section.title}
                ariaLabel="Section title"
                placeholder="Section title"
                onCommit={(next) => handlers.onRenameSection(section.id, next || 'Section')}
                className="text-lg font-medium tracking-headline text-foreground"
              />
              <InlineText
                value={section.description ?? ''}
                ariaLabel="Section description"
                placeholder="Add a short description (optional)"
                onCommit={(next) => handlers.onDescribeSection(section.id, next)}
                className="mt-0.5 text-sm text-muted-foreground"
              />
            </>
          ) : (
            <>
              <button
                type="button"
                onClick={() => handlers.onToggleCollapse(section.id)}
                aria-expanded={!collapsed}
                className="-ml-1 flex max-w-full items-center gap-1 rounded-md px-1 text-left hover:bg-secondary/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                {collapsed ? (
                  <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" />
                ) : (
                  <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground" />
                )}
                <h2 className="truncate text-lg font-medium tracking-headline text-foreground">{section.title}</h2>
                {collapsed && (
                  <span className="ml-1 shrink-0 text-xs text-muted-foreground">
                    {count} {count === 1 ? 'widget' : 'widgets'}
                  </span>
                )}
              </button>
              {section.description && !collapsed && <p className="text-sm text-muted-foreground">{section.description}</p>}
            </>
          )}
        </div>

        {editing && (
          <div className="flex shrink-0 items-center gap-1">
            {!isMobile && <AddWidgetMenu label="Add" onPick={(kind) => handlers.onAddWidget(kind, section.id)} />}
            <Button variant="ghost" size="icon" className="h-8 w-8" aria-label="Move section up" disabled={isFirst} onClick={() => handlers.onMoveSection(section.id, -1)}>
              <ArrowUp className="h-4 w-4" />
            </Button>
            <Button variant="ghost" size="icon" className="h-8 w-8" aria-label="Move section down" disabled={isLast} onClick={() => handlers.onMoveSection(section.id, 1)}>
              <ArrowDown className="h-4 w-4" />
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" className="h-8 w-8" aria-label="Section options">
                  <MoreHorizontal className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-52">
                {isMobile && (
                  <>
                    <DropdownMenuItem onSelect={() => handlers.onAddWidget('metric', section.id)}>Add metric</DropdownMenuItem>
                    <DropdownMenuItem onSelect={() => handlers.onAddWidget('chart', section.id)}>Add chart</DropdownMenuItem>
                    <DropdownMenuItem onSelect={() => handlers.onAddWidget('table', section.id)}>Add table</DropdownMenuItem>
                    <DropdownMenuItem onSelect={() => handlers.onAddWidget('forecast', section.id)}>Add forecast</DropdownMenuItem>
                    <DropdownMenuItem onSelect={() => handlers.onAddWidget('text', section.id)}>Add note</DropdownMenuItem>
                    <DropdownMenuSeparator />
                  </>
                )}
                <DropdownMenuItem onSelect={() => handlers.onToggleCollapse(section.id)}>
                  {collapsed ? <ChevronsUpDown className="mr-2 h-4 w-4" /> : <ChevronsDownUp className="mr-2 h-4 w-4" />}
                  {collapsed ? 'Expand section' : 'Collapse section'}
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem className="text-fresh-salmon-dark focus:text-fresh-salmon-dark" onSelect={() => handlers.onRemoveSection(section.id)}>
                  <Trash2 className="mr-2 h-4 w-4" /> Remove section
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        )}
      </div>

      {collapsed ? null : (
        <div
          ref={gridRef}
          className="grid grid-cols-1 gap-4 md:grid-cols-12"
          style={{ gridAutoRows: `${GRID_ROW_PX}px` }}
          onDragOver={editing ? (e) => handlers.onDragOverSection(e, section.id, endIndex) : undefined}
          onDrop={editing ? handlers.onDrop : undefined}
        >
          {section.widgets.map((widget, index) => {
            const indicator =
              dropTarget && dropTarget.sectionId === section.id
                ? dropTarget.index === index
                  ? 'before'
                  : index === count - 1 && dropTarget.index === count
                    ? 'after'
                    : null
                : null;
            return (
              <WidgetFrame
                key={widget.id}
                widget={widget}
                sectionId={section.id}
                index={index}
                isFirst={index === 0}
                isLast={index === count - 1}
                dropIndicator={indicator}
                isDragging={dragWidgetId === widget.id}
                getGridMetrics={getGridMetrics}
                onEdit={handlers.onEdit}
                onDuplicate={handlers.onDuplicate}
                onRemove={handlers.onRemove}
                onNudge={handlers.onNudge}
                onResize={handlers.onResize}
                onDragStart={handlers.onDragStart}
                onDragEnd={handlers.onDragEnd}
                onDragOverWidget={handlers.onDragOverWidget}
                onDrop={handlers.onDrop}
              />
            );
          })}

          {editing && count === 0 && (
            <div
              className={cn(
                'flex flex-col items-center justify-center gap-2 rounded-lg border border-dashed px-4 text-center text-sm text-muted-foreground transition-colors',
                targetsEnd ? 'border-atmosphere-dark bg-atmosphere/20' : 'border-border',
              )}
              style={{ gridColumn: '1 / -1', gridRow: 'span 1' }}
            >
              <span>{dragging ? 'Drop here' : 'This section is empty.'}</span>
              {!dragging && <AddWidgetMenu label="Add a widget" variant="secondary" align="start" onPick={(kind) => handlers.onAddWidget(kind, section.id)} />}
            </div>
          )}

          {editing && count > 0 && dragging && !isMobile && (
            <div
              aria-hidden
              className={cn(
                'flex items-center justify-center rounded-lg border border-dashed text-xs text-muted-foreground transition-colors',
                targetsEnd ? 'border-atmosphere-dark bg-atmosphere/20 text-foreground' : 'border-border',
              )}
              style={{ gridColumn: 'span 2 / span 2', gridRow: 'span 1' }}
            >
              Drop at end
            </div>
          )}
        </div>
      )}

      {!editing && !collapsed && count === 0 && <p className="text-sm text-muted-foreground">Nothing in this section yet.</p>}
    </section>
  );
}

export const DashboardSectionView = memo(DashboardSectionViewImpl);
